import { useCallback, useEffect, useState } from 'react'
import { useAppDispatch } from '@/app/hooks'
import { updateUser } from '@/features/auth/slices/authSlice'
import { addNotification } from '@/features/common/slices/uiSlice'
import { updateStoredUser } from '@/core/auth'
import { profileRepository } from '@/data/profile/repositories/profile_repository_impl'
import type { AdminProfile } from '@/domain/profile/entities/admin_profile.entity'
import { GetAdminProfileUseCase } from '@/domain/profile/usecases/get_admin_profile.usecase'
import { UpdateAdminProfileUseCase } from '@/domain/profile/usecases/update_admin_profile.usecase'
import { extractRtkErrorMessage } from '@/services/apiErrors'

const defaultGetAdminProfile = new GetAdminProfileUseCase(profileRepository)
const defaultUpdateAdminProfile = new UpdateAdminProfileUseCase(profileRepository)

export interface UseAdminProfileViewModelDeps {
  getAdminProfile?: GetAdminProfileUseCase
  updateAdminProfile?: UpdateAdminProfileUseCase
}

export function useAdminProfileViewModel(deps: UseAdminProfileViewModelDeps = {}) {
  const dispatch = useAppDispatch()
  const getAdminProfile = deps.getAdminProfile ?? defaultGetAdminProfile
  const updateAdminProfile = deps.updateAdminProfile ?? defaultUpdateAdminProfile

  const [profile, setProfile] = useState<AdminProfile | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<unknown>(null)
  const [name, setName] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const loadProfile = useCallback(async () => {
    setIsLoading(true)
    setLoadError(null)
    try {
      const data = await getAdminProfile.execute()
      setProfile(data)
      setName(data.name)
    } catch (error: unknown) {
      setLoadError(error)
      setProfile(null)
    } finally {
      setIsLoading(false)
    }
  }, [getAdminProfile])

  useEffect(() => {
    void loadProfile()
  }, [loadProfile])

  useEffect(() => {
    if (profile?.name) {
      setName(profile.name)
    }
  }, [profile?.name])

  const handleNameChange = useCallback((value: string) => {
    setName(value)
    setFormError(null)
  }, [])

  const handleReset = useCallback(() => {
    setName(profile?.name ?? '')
    setFormError(null)
  }, [profile?.name])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setFormError(null)

      const trimmed = name.trim()
      if (!trimmed) {
        setFormError('Display name is required.')
        return
      }

      if (profile && trimmed === profile.name) {
        dispatch(addNotification({ message: 'No changes to save.', type: 'info' }))
        return
      }

      setIsSaving(true)
      try {
        const updated = await updateAdminProfile.execute({ name: trimmed })
        setProfile(updated)
        dispatch(updateUser({ name: updated.name }))
        updateStoredUser({ name: updated.name })
        dispatch(addNotification({ message: 'Profile updated successfully.', type: 'success' }))
      } catch (err: unknown) {
        setFormError(extractRtkErrorMessage(err))
      } finally {
        setIsSaving(false)
      }
    },
    [dispatch, name, profile, updateAdminProfile]
  )

  return {
    profile,
    isLoading,
    loadError,
    name,
    formError,
    isSaving,
    handleNameChange,
    handleReset,
    handleSubmit,
    refetch: loadProfile,
  }
}
